<template>
  <!-- Add Custom Tool Dialog -->
  <ApplicationDrawer
    v-model:show-drawer="showEditDialog"
    :custom-application-template="selectedApplication"
    :is-already-existing-application="false"
    :is-name-equals-to-search="createManuallyApplication"
    :adding-application-from-template="selectedApplication ? true : false"
    @application-deleted="emit('applicationDeleted')" />

  <SmDrawer
    v-model:visibility="showApplicationDrawer"
    :title="i18n.t('drawers.addApplicationDrawer.title')">
    <div class="mb-2 flex h-full w-full flex-col overflow-hidden">
      <SmInput
        v-model="applicationFilter"
        class="mb-2"
        :label="i18n.t('drawers.addApplicationDrawer.searchPlaceholder')"
        size="small"
        outline />

      <div
        class="application-card mb-3"
        @click="
          () => {
            if (filteredApplication.length !== 0) {
              showEditDialog = true
              selectedApplication = undefined
            } else createManuallyManagedApplication()
          }
        ">
        <div class="application-wrapper" data-test-id="add-custom-application">
          <div
            id="application-manually"
            class="tool-desc-text"
            style="margin-left: 0">
            <span class="application-name">{{
              filteredApplication.length === 0
                ? i18n.t(
                    'drawers.addApplicationDrawer.searchedSoftwareNotFound.name',
                    {
                      name: applicationFilter,
                    }
                  )
                : i18n.t('drawers.addApplicationDrawer.manuallySoftware.name')
            }}</span>
            <div class="application-type">
              {{
                filteredApplication.length === 0
                  ? i18n.t(
                      'drawers.addApplicationDrawer.searchedSoftwareNotFound.type'
                    )
                  : i18n.t('drawers.addApplicationDrawer.manuallySoftware.type')
              }}
            </div>
          </div>
          <ChevronRight :size="28" />
        </div>
      </div>

      <!-- Software List -->

      <VList
        v-slot="{ item }"
        :data="filteredApplication"
        :style="{ height: 'calc(100% - 80px)' }">
        <div :key="item.name + item.type" class="application-card">
          <ApplicationAddDrawerSoftwareItem
            :application="item"
            @create-custom-application="
              (applicationInformation, settings) =>
                handleApplicationAddButtonClick(
                  applicationInformation,
                  settings
                )
            "
            @close-drawer="showApplicationDrawer = false" />
        </div>
      </VList>
    </div>
  </SmDrawer>
</template>

<script setup lang="ts">
  import { useI18n } from 'vue-i18n'
  import { useRoute, useRouter } from 'vue-router'

  import {
    SoftwareInformation,
    SoftwareIntegrationsService,
    SoftwarePostIn,
    SoftwareStatus,
    SoftwareTypes,
  } from '@/client'
  import { useGlobalStore } from '@/stores/globalStore'
  import { ConnectSettings } from './ApplicationAddDrawerSoftwareItem.vue'
  import { VList } from 'virtua/vue'
  import { ChevronRight } from 'lucide-vue-next'

  const store = useGlobalStore()
  const router = useRouter()
  const route = useRoute()
  const i18n = useI18n()

  // Props and Emits
  const props = defineProps({
    addApplicationDrawer: {
      type: Boolean,
      default: false,
    },
  })
  const emit = defineEmits([
    'update:addApplicationDrawer',
    'createCustomApplication',
    'applicationDeleted',
  ])

  const showEditDialog = ref<boolean>(
    route.query['open-create-custom-application-drawer'] === 'true'
  )
  const selectedApplication = ref<SoftwarePostIn>()

  const showApplicationDrawer = computed({
    get: (): boolean => props.addApplicationDrawer,
    set: (value: boolean): void => emit('update:addApplicationDrawer', value),
  })

  // Load available Integrations
  const availableIntegrations = ref<SoftwareInformation[]>([])
  const loading = ref<boolean>(false)

  onMounted(async () => {
    await loadIntegrations()
  })

  async function loadIntegrations() {
    loading.value = true
    const response =
      await SoftwareIntegrationsService.getIntegrationsApiV1SoftwareIntegrationsGet(
        {}
      )

    availableIntegrations.value = response.toSorted((a, b) =>
      a.display_name.toLocaleLowerCase() > b.display_name.toLocaleLowerCase()
        ? 1
        : -1
    )

    loading.value = false
  }

  // This ref is used to keep the name input not disabled
  const createManuallyApplication = ref<boolean>(false)

  // This function will be triggered if the user searches a application which sonst exist
  // And it opens the EditSoftwareDrawer with the name prefilled with the searched name
  function createManuallyManagedApplication() {
    createManuallyApplication.value = true
    selectedApplication.value = {
      software_name: applicationFilter.value,
      type: SoftwareTypes.MANUALLY,
      data: {},
    }
    showEditDialog.value = true
  }

  function handleApplicationAddButtonClick(
    application: SoftwareInformation,
    settings: ConnectSettings
  ) {
    if (
      application.custom_authorization_flow_required === true ||
      application.type === SoftwareTypes.BOT ||
      application.type === SoftwareTypes.TOKEN ||
      application.type === SoftwareTypes.OAUTH
    ) {
      const query: Record<string, string> = {
        softwareName: application.name,
        softwareType: application.type,
        readonly: settings.readonly ? 'true' : 'false',
        accessToken: store.getJWT() as string,
      }

      if (application.custom_authorization_flow_required) {
        query['customAuthorizationFlow'] = 'true'
      }

      const routeData = router.resolve({
        path: 'authorize',
        query: query,
      })
      showApplicationDrawer.value = false
      window.open(routeData.href, '_blank')
    } else {
      showEditDialog.value = true
      selectedApplication.value = {
        software_name: application.name,
        type: application.type,
      }
    }
  }

  // Filter Logic
  const applicationFilter = ref('')
  const filteredApplication = computed<SoftwareInformation[]>(() => {
    return availableIntegrations.value.filter((val: SoftwareInformation) => {
      // Filter work in progress
      if (val.status === SoftwareStatus.WORK_IN_PROGRESS) return false

      return (
        val.name
          .toLowerCase()
          .includes(applicationFilter.value.toLowerCase()) ||
        val.display_name
          .toLowerCase()
          .includes(applicationFilter.value.toLowerCase())
      )
    })
  })

  watch(
    () => route.query,
    () => {
      if (route.query['open-create-custom-application-drawer']) {
        selectedApplication.value = undefined
        showEditDialog.value =
          route.query['open-create-custom-application-drawer'] === 'true'
        router.push('/applications')
      }

      if (route.query['open-add-application-drawer']) {
        showApplicationDrawer.value =
          route.query['open-add-application-drawer'] === 'true'
        router.push('/applications')
      }

      if (route.query['add-application-drawer-search-query']) {
        applicationFilter.value = route.query[
          'add-application-drawer-search-query'
        ] as string
        router.push('/applications')
      }
    }
  )
</script>

<style lang="scss">
  /* Single Application */
  .application-wrapper {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  }

  .application-card {
    width: 100%;
    cursor: pointer;
    padding: 1.5rem;
    border: 1px solid theme('colors.gray[300]');
    border-radius: var(--border-radius);
    margin-bottom: 0.75rem;
  }

  .dark .application-card {
    border: 1px solid var(--sm-cta);
  }

  .application-card:hover {
    background-color: var(--el-table-row-hover-bg-color);
  }

  .application-image {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 40px;
    width: 40px;
  }

  .application-name {
    font-size: 1.1rem;
    line-height: 1;
  }

  .tool-desc {
    display: flex;
  }

  .tool-desc-text {
    margin-left: 1rem;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
  }

  .application-type {
    color: theme('colors.gray[500]');
  }
</style>
